import type { ProductInfo } from "@expert/contentful";
import type { Partner } from "@expert/shared-types";

import { Badge, Box, Card, Flex, Image, Stack, Text } from "@mantine/core";
import clsx from "clsx";

import { useSalesContext } from "../../../../../sales-context/SalesContext";
import { SaleProgress } from "../../../../../types";
import { PlanBadge } from "./PlanBadge";
import { determineCardStyle, getPartnerIcon, showRecommended } from "./PlanCard.helpers";
import classes from "./PlanCard.module.css";

export interface CompactPlanCardProps {
    saleProgress: SaleProgress;
    partner: Partner;
    product: ProductInfo | undefined;
    onClick: () => void;
    isSelected?: boolean;
    productSku: string;
    position?: number;
}

export const CompactPlanCard = ({
    saleProgress,
    partner,
    product,
    onClick,
    isSelected = false,
    productSku,
    position,
}: CompactPlanCardProps) => {
    const { sessionId, features } = useSalesContext();

    const isSalesQuestionnaireEnabled = features.isSalesQuestionnaireEnabled;
    if (!product) {
        return null;
    }
    const isProductRecommended = (position === 0 && isSalesQuestionnaireEnabled) || product.isRecommended;

    const isClickable = saleProgress === SaleProgress.InProgress && typeof onClick === "function";
    const handleClick = isClickable ? onClick : undefined;
    const { displayName, logo } = product;
    const uniqueId = `${displayName}-card`;
    const isEnrolledOrDeclined = ([SaleProgress.Success, SaleProgress.Declined] as SaleProgress[]).includes(
        saleProgress,
    );
    const isInProgressOrFailure = ([SaleProgress.InProgress, SaleProgress.Failure] as SaleProgress[]).includes(
        saleProgress,
    );

    return (
        <Card
            data-testid={uniqueId}
            classNames={{
                root: clsx(
                    determineCardStyle(saleProgress),
                    isSelected ? classes.planCardSelected : classes.cardContainer,
                ),
            }}
            onClick={handleClick}
            radius="md"
            shadow="sm"
            mih={96}
            h="auto"
        >
            {saleProgress === SaleProgress.Failure && (
                <Badge
                    color={saleProgress === SaleProgress.Failure ? "error.3" : "warning.3"}
                    fz="xxs"
                    className={classes.badge}
                    mb={"xs"}
                >
                    {saleProgress === SaleProgress.Failure ? "Ineligible" : "Check eligibility"}
                </Badge>
            )}

            {getPartnerIcon(partner)}

            <Stack gap={8} mt={"xs"}>
                <Text classNames={{ root: classes.cardText }} size="sm" pr={4}>
                    {displayName}
                </Text>

                <Flex
                    className={isInProgressOrFailure ? "" : classes.hidden}
                    classNames={{ root: classes.planImageSection }}
                >
                    {logo && (
                        <Box flex={1} style={{ display: "flex", alignItems: "baseline" }}>
                            <Image
                                src={logo}
                                mah={50}
                                maw={50}
                                fit="contain"
                                data-testid={`${displayName}-image`}
                                alt={`${displayName} icon`}
                            />
                        </Box>
                    )}
                </Flex>
            </Stack>

            {isInProgressOrFailure ? (
                <Stack classNames={{ root: classes.offerStatusSection }}>
                    {isProductRecommended && showRecommended(partner, productSku, sessionId) && (
                        <PlanBadge text="Recommended" testId={`${displayName}-recommended-badge`} />
                    )}

                    {!isEnrolledOrDeclined && isSelected && (
                        <PlanBadge className={classes.selectedBadge} text="Started" />
                    )}
                </Stack>
            ) : (
                <Flex classNames={{ root: classes.offerStatusSection }}>
                    {isEnrolledOrDeclined && (
                        <PlanBadge
                            text={saleProgress === SaleProgress.Success ? "Enrolled" : "Declined"}
                            className={
                                saleProgress === SaleProgress.Success ? classes.enrolledBadge : classes.declinedBadge
                            }
                        />
                    )}
                </Flex>
            )}
        </Card>
    );
};
